import React, { useState, useEffect, useCallback } from 'react';
import { Panel } from 'reactflow';
import { Timeline, Typography, Badge, Button } from 'antd';
import {
    RobotOutlined,
    CheckCircleOutlined,
    LoadingOutlined,
    ClockCircleOutlined,
    CloseOutlined,
    ExpandOutlined,
    CompressOutlined,
    SyncOutlined
} from '@ant-design/icons';
import api from '../../../api/client';

const { Text } = Typography;

/**
 * AgentTimelinePanel - Floating panel showing agent activity on canvas
 * Polls the database for real-time agent activity updates
 * Only visible when agents have taken actions on this canvas
 */
const AgentTimelinePanel = ({ canvasId, nodes, inline = false, refreshKey, hasActiveWork, activities }) => {
    const [visible, setVisible] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch agent activity from the database
    const fetchAgentActivity = useCallback(async () => {
        if (!canvasId) return;

        try {
            // Fetch media generations for this canvas where node_id is 'mcp_agent'
            const response = await api.get(`/media-generations?canvas_id=${canvasId}`);
            const mediaGenerations = response.data || [];

            // Filter for agent-generated content
            const agentMedia = mediaGenerations.filter(m =>
                m.node_id === 'mcp_agent' ||
                m.metadata?.node_id === 'mcp_agent'
            );

            const agentEvents = [];

            // Add media generation events
            agentMedia.forEach(media => {
                agentEvents.push({
                    id: media.id,
                    type: 'media_generated',
                    title: media.model || 'Generated Image',
                    prompt: media.prompt?.substring(0, 60) + (media.prompt?.length > 60 ? '...' : ''),
                    time: media.created_at ? new Date(media.created_at).toLocaleTimeString() : 'Now',
                    createdAt: media.created_at,
                    status: media.status === 'COMPLETED' ? 'completed' :
                        media.status === 'IN_PROGRESS' ? 'running' : 'pending',
                    url: media.url
                });
            });

            // Check for agent-created nodes (nodes with isUpload and agent labels)
            const agentNodes = (nodes || []).filter(n =>
                n.data?.isAgentReceipt ||
                (n.data?.isUpload && n.data?.label?.toLowerCase().includes('agent'))
            );

            agentNodes.forEach(node => {
                agentEvents.push({
                    id: node.id,
                    type: 'node_created',
                    title: node.data?.label || 'Agent Placed Media',
                    time: new Date().toLocaleTimeString(),
                    status: 'completed'
                });
            });

            // Sort by creation time (most recent first)
            agentEvents.sort((a, b) => {
                if (a.createdAt && b.createdAt) {
                    return new Date(b.createdAt) - new Date(a.createdAt);
                }
                return 0;
            });

            setEvents(agentEvents);

            // Show panel if there's agent activity
            if (agentEvents.length > 0) {
                setVisible(true);
            }
        } catch (error) {
            console.error('Failed to fetch agent activity:', error);
        }
    }, [canvasId, nodes]);

    // Fetch on mount and when refreshKey changes (triggered by parent's smart polling)
    useEffect(() => {
        fetchAgentActivity();
    }, [fetchAgentActivity, refreshKey]);

    // Manual refresh
    const handleRefresh = async () => {
        setLoading(true);
        await fetchAgentActivity();
        setLoading(false);
    };

    // Don't render if no agent activity
    if (!visible || events.length === 0) {
        return null;
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckCircleOutlined style={{ color: '#3fb950' }} />;
            case 'running':
                return <LoadingOutlined style={{ color: '#58a6ff' }} spin />;
            case 'pending':
                return <ClockCircleOutlined style={{ color: '#d29922' }} />;
            default:
                return <RobotOutlined style={{ color: '#58a6ff' }} />;
        }
    };

    const getEventColor = (type) => {
        switch (type) {
            case 'media_generated':
                return '#58a6ff';
            case 'node_created':
                return '#3fb950';
            case 'canvas_created':
                return '#a371f7';
            default:
                return '#8b949e';
        }
    };

    const panelContent = (
        <div style={{ pointerEvents: 'auto' }}>
            <div style={{
                background: 'rgba(22, 27, 34, 0.95)',
                backdropFilter: 'blur(8px)',
                borderRadius: 12,
                border: '1px solid #30363d',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                padding: collapsed ? '6px 10px' : '16px',
                minWidth: collapsed ? 'auto' : 280,
                maxWidth: 320,
                maxHeight: collapsed ? 'auto' : 400,
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: collapsed ? 0 : 12,
                    borderBottom: collapsed ? 'none' : '1px solid #30363d',
                    paddingBottom: collapsed ? 0 : 12
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Badge count={events.length} size="small" style={{ backgroundColor: '#58a6ff' }}>
                            <RobotOutlined style={{ fontSize: 18, color: '#58a6ff' }} />
                        </Badge>
                        {!collapsed && (
                            <Text strong style={{ color: '#fff', fontSize: 14 }}>
                                Agent Activity
                            </Text>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: collapsed ? 2 : 4 }}>
                        {!collapsed && (
                            <Button
                                type="text"
                                size="small"
                                icon={<SyncOutlined spin={loading} />}
                                onClick={handleRefresh}
                                style={{ color: '#8b949e' }}
                            />
                        )}
                        <Button
                            type="text"
                            size="small"
                            icon={collapsed ? <ExpandOutlined /> : <CompressOutlined />}
                            onClick={() => setCollapsed(!collapsed)}
                            style={{ color: '#8b949e' }}
                        />
                        {!collapsed && (
                            <Button
                                type="text"
                                size="small"
                                icon={<CloseOutlined />}
                                onClick={() => setVisible(false)}
                                style={{ color: '#8b949e' }}
                            />
                        )}
                    </div>
                </div>

                {/* Timeline Content */}
                {!collapsed && (
                    <div style={{
                        overflowY: 'auto',
                        maxHeight: 300,
                        paddingRight: 8
                    }}>
                        <Timeline
                            style={{ marginTop: 8 }}
                            items={events.slice(0, 10).map(event => ({
                                dot: getStatusIcon(event.status),
                                color: getEventColor(event.type),
                                children: (
                                    <div style={{ marginLeft: 4 }}>
                                        <Text style={{ color: '#fff', fontSize: 13, display: 'block' }}>
                                            {event.title}
                                        </Text>
                                        {event.prompt && (
                                            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontStyle: 'italic', display: 'block' }}>
                                                "{event.prompt}"
                                            </Text>
                                        )}
                                        <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, display: 'block' }}>
                                            {event.time}
                                        </Text>
                                    </div>
                                )
                            }))}
                        />
                        {events.length > 10 && (
                            <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, textAlign: 'center', display: 'block' }}>
                                + {events.length - 10} more events
                            </Text>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    // If inline mode, render without Panel wrapper
    if (inline) {
        return panelContent;
    }

    // Otherwise render inside ReactFlow Panel
    return (
        <Panel position="top-right" style={{ zIndex: 999, marginTop: 70, marginRight: 12 }}>
            {panelContent}
        </Panel>
    );
};

export default AgentTimelinePanel;
